import { useCallback, useRef, useState } from 'react';
import { useAutosaveDraft, useDrafts } from 'features/drafts';
import { FORM_FIELDS_MAP, NEW_CANDIDATE_ENDPOINTS } from 'features/new-candidate/constants';
import useDebounce from 'hooks/debounce';
import { useCheckEmail } from 'hooks/useCheckEmail';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { DELAY_BOUNCE_TIME } from 'UI/constants/defaults';
import { EntityType } from 'UI/constants/entityTypes';
import { areAttachmentsEquivalent } from 'UI/utils/attachments';
import { selectValidation } from 'UI/utils/forms';

export { areAttachmentsEquivalent };

const isDraftSafeAttachment = attachment => {
  const id = attachment?.id;

  return (
    id !== undefined &&
    id !== null &&
    id !== 0 &&
    id !== '0' &&
    !attachment?.hasError &&
    !attachment?.isLoading
  );
};

export const normalizeDraftSafeAttachments = attachments =>
  (attachments ?? []).filter(isDraftSafeAttachment);

const getAttachmentId = attachment => attachment?.id;

const mergeDraftSafeAttachments = (reportedAttachments, currentAttachments) => {
  const currentById = new Map(
    currentAttachments.map(attachment => [getAttachmentId(attachment), attachment])
  );

  return reportedAttachments.map(attachment => {
    if (!attachment?.isLoading) return attachment;

    return currentById.get(getAttachmentId(attachment)) ?? attachment;
  });
};

export const useDataSheet = ({ setValue }) => {
  const [isDataSheetOpen, setIsDataSheetOpen] = useState(false);

  const openDataSheet = () => {
    setIsDataSheetOpen(true);
  };

  const closeDataSheet = () => {
    setIsDataSheetOpen(false);
  };

  const handleSaveCandidateDataSheet = useCallback(
    (candidateDataSheetID, progress) => {
      setValue(FORM_FIELDS_MAP.DataSheet.key, { id: candidateDataSheetID, progress });
    },
    [setValue]
  );

  return {
    closeDataSheet,
    handleSaveCandidateDataSheet,
    isDataSheetOpen,
    openDataSheet
  };
};

export const useAttachments = ({ initialAttachments }) => {
  const initialDraftSafeAttachments = normalizeDraftSafeAttachments(initialAttachments);
  const [attachments, setAttachments] = useState(initialDraftSafeAttachments);
  const attachmentsRef = useRef(initialDraftSafeAttachments);
  const initialAttachmentsRef = useRef(initialDraftSafeAttachments);
  const hasSyncedFileManagerRef = useRef(false);

  const handleAttachmentsChanged = useCallback(data => {
    const reportedAttachments = flatMap(map(data, 'files'));

    const attachmentsChanged = normalizeDraftSafeAttachments(
      mergeDraftSafeAttachments(reportedAttachments, attachmentsRef.current)
    );
    const isBootstrapEmptySync =
      !hasSyncedFileManagerRef.current &&
      initialAttachmentsRef.current.length > 0 &&
      attachmentsChanged.length === 0;

    if (isBootstrapEmptySync) {
      return {
        attachments: attachmentsRef.current,
        hasChanged: false
      };
    }

    const hasChanged = hasSyncedFileManagerRef.current
      ? !areAttachmentsEquivalent(attachmentsRef.current, attachmentsChanged)
      : !areAttachmentsEquivalent(initialAttachmentsRef.current, attachmentsChanged);

    hasSyncedFileManagerRef.current = true;
    attachmentsRef.current = attachmentsChanged;
    setAttachments(attachmentsChanged);

    return {
      attachments: attachmentsChanged,
      hasChanged
    };
  }, []);

  return {
    attachments,
    handleAttachmentsChanged
  };
};

export const useRegisteredEmails = ({ email, isFromDirectory, isFromTalentPool, otherEmail }) => {
  const debouncedEmail = useDebounce(email ?? '', DELAY_BOUNCE_TIME).trim();
  const debouncedOtherEmail = useDebounce(otherEmail ?? '', DELAY_BOUNCE_TIME).trim();

  const { validation: emailVerification, initialData } = useCheckEmail({
    debouncedEmail,
    hasEmail: !!email,
    isFromTalentPool,
    shouldCheckContacts: !isFromDirectory
  });

  const { validation: otherEmailVerification } = useCheckEmail({
    debouncedEmail: debouncedOtherEmail,
    hasEmail: !!otherEmail,
    isFromTalentPool,
    shouldCheckContacts: !isFromDirectory
  });

  const emailsValidation = selectValidation(
    { ...emailVerification, email: debouncedEmail },
    { ...otherEmailVerification, email: debouncedOtherEmail },
    initialData
  );

  return emailsValidation;
};

const getPreselectedCompanyKey = preselectedCompany => {
  if (!preselectedCompany) return null;

  const id = preselectedCompany?.id ?? preselectedCompany;
  return id ? `preselected-company:${id}` : null;
};

const getLoadedCandidateKey = formattedResults => {
  if (!formattedResults || formattedResults?.draftId) return null;

  const source = formattedResults?.fromSource;
  const sourceId =
    formattedResults?.id ??
    formattedResults?.applicantId ??
    formattedResults?.contactId ??
    formattedResults?.hiringId ??
    formattedResults?.fromContact?.id;

  return source && sourceId ? `${source}:${sourceId}` : null;
};

export const getCandidateAutosaveOnReadyKey = ({ formattedResults, preselectedCompany }) => {
  const loadedCandidateKey = getLoadedCandidateKey(formattedResults);
  const preselectedCompanyKey = getPreselectedCompanyKey(preselectedCompany);

  if (loadedCandidateKey && preselectedCompanyKey) {
    return `${loadedCandidateKey}:${preselectedCompanyKey}`;
  }

  if (formattedResults?.draftId) {
    return preselectedCompanyKey
      ? `draft:${formattedResults.draftId}:${preselectedCompanyKey}`
      : null;
  }

  return loadedCandidateKey ?? preselectedCompanyKey;
};

export const useCandidateDraftAutosave = ({
  attachments,
  canSaveDraft,
  defaultValues,
  formattedResults,
  getFormValues,
  preselectedCompany
}) => {
  const draft = useDrafts({
    initialDraft: defaultValues,
    entity: EntityType.Candidate,
    endpoint: NEW_CANDIDATE_ENDPOINTS.drafts
  });

  const autosave = useAutosaveDraft({
    attachments,
    autosaveOnReadyKey: getCandidateAutosaveOnReadyKey({ formattedResults, preselectedCompany }),
    canSaveDraft,
    getFormValues,
    lastSavedSnapshotRef: draft.lastSavedSnapshotRef,
    saveDraft: draft.saveDraft
  });

  return {
    ...draft,
    ...autosave
  };
};
